import { NAO_CONFORME, rotuloTipoCampo, type Resposta } from '@qsms/shared'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate, useParams } from 'react-router-dom'
import { ExcluirInspecao } from '../componentes/ExcluirInspecao.js'
import { GaleriaFotos } from '../componentes/GaleriaFotos.js'
import { banco } from '../dados/banco.js'
import { useArquivo } from '../dados/useArquivo.js'
import { useUsuarioAtual } from '../dados/usuarioAtual.js'
import { EtiquetaCriticidade, EtiquetaStatus } from './Inicio.js'

/// Visualização somente leitura. É o que se abre para uma inspeção já assinada, ou
/// para uma que veio do servidor (feita por outra pessoa).

export function Detalhe() {
  const { id } = useParams<{ id: string }>()
  const navegar = useNavigate()
  const { usuario } = useUsuarioAtual()

  const inspecao = useLiveQuery(() => (id ? banco.inspecoes.get(id) : undefined), [id])
  const checklist = useLiveQuery(
    () => (inspecao ? banco.checklists.get(inspecao.checklistId) : undefined),
    [inspecao?.checklistId],
  )
  const obra = useLiveQuery(
    () => (inspecao ? banco.obras.get(inspecao.obraId) : undefined),
    [inspecao?.obraId],
  )

  if (inspecao === undefined) return <p className="py-10 text-center text-slate-500">Carregando…</p>
  if (inspecao === null) {
    return (
      <div className="cartao p-6 text-center">
        <p className="font-semibold text-slate-700">Inspeção não encontrada neste aparelho.</p>
        <p className="mt-1 text-sm text-slate-500">
          Conecte-se à internet para baixar as inspeções da obra.
        </p>
        <button onClick={() => navegar('/')} className="botao-primario mt-4">
          Voltar
        </button>
      </div>
    )
  }

  const respostas = [...inspecao.respostas].sort((a, b) => a.ordem - b.ordem)

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <button onClick={() => navegar('/')} className="text-sm font-medium text-marinho">
          ← Voltar
        </button>
        {/* Esta é a tela das inspeções que não se editam: de outra pessoa, de
            outro aparelho, ou assinadas. Apagar uma dessas é poder de
            administrador — a API recusa os demais de qualquer forma. */}
        {usuario?.admin && <ExcluirInspecao inspecao={inspecao} />}
      </div>

      <div className="cartao mb-4 p-4">
        <p className="font-mono text-xs text-slate-500">{inspecao.codigo}</p>
        <h2 className="text-lg font-bold text-slate-900">{inspecao.titulo || checklist?.nome}</h2>
        {inspecao.titulo && checklist && <p className="text-sm text-slate-500">{checklist.nome}</p>}

        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <Item rotulo="Obra" valor={obra ? `${obra.codigo} — ${obra.nome}` : '—'} />
          <Item
            rotulo="Registrada em"
            valor={new Date(inspecao.registradoEm).toLocaleString('pt-BR')}
          />
        </dl>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <EtiquetaStatus status={inspecao.status} />
          {inspecao.criticidadeApia && <EtiquetaCriticidade criticidade={inspecao.criticidadeApia} />}
        </div>

        {inspecao.assinaturaUsuario && (
          <p className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500">
            Assinado por {inspecao.assinaturaUsuario}
          </p>
        )}
      </div>

      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Respostas</h3>
      {respostas.length === 0 ? (
        <p className="cartao p-6 text-center text-sm text-slate-500">Nenhuma resposta registrada.</p>
      ) : (
        <ul className="mb-4 grid gap-2 lg:grid-cols-2">
          {respostas.map((r) => (
            <li key={r.id} className="cartao p-3">
              <p className="text-xs font-semibold text-slate-500">{r.campoNome}</p>
              <ValorLegivel resposta={r} />
              <Tratativa resposta={r} />
            </li>
          ))}
        </ul>
      )}

      <GaleriaFotos inspecaoId={inspecao.id} fotos={inspecao.fotos} somenteLeitura onMudar={() => {}} />
    </section>
  )
}

function Item({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{rotulo}</dt>
      <dd className="font-medium text-slate-800">{valor}</dd>
    </div>
  )
}

function ValorLegivel({ resposta }: { resposta: Resposta }) {
  switch (resposta.campoTipo) {
    case 'SIM_NAO':
      if (resposta.booleano === null || resposta.booleano === undefined) return <Vazio />
      return (
        <p className={`font-semibold ${resposta.booleano ? 'text-leve' : 'text-alta'}`}>
          {resposta.booleano ? 'Sim' : 'Não'}
        </p>
      )

    case 'MULTISSELECAO': {
      const opcoes = resposta.opcoes ?? []
      if (opcoes.length === 0) return <Vazio />
      return (
        <div className="mt-1 flex flex-wrap gap-1">
          {opcoes.map((o) => (
            <span key={o} className="etiqueta bg-slate-100 text-slate-700">
              {o}
            </span>
          ))}
        </div>
      )
    }

    case 'DATA':
      return resposta.data ? (
        <p className="text-slate-800">
          {new Date(`${resposta.data}T00:00:00`).toLocaleDateString('pt-BR')}
        </p>
      ) : (
        <Vazio />
      )

    case 'NUMERO':
      return resposta.numero !== null && resposta.numero !== undefined ? (
        <p className="text-slate-800">{resposta.numero.toLocaleString('pt-BR')}</p>
      ) : (
        <Vazio />
      )

    case 'IMAGEM':
    case 'ASSINATURA':
      if (resposta.arquivoId) return <ImagemDaResposta resposta={resposta} />
      return resposta.texto ? <p className="text-slate-800">{resposta.texto}</p> : <Vazio />

    default: {
      const texto = resposta.textoLongo ?? resposta.texto
      return texto ? <p className="whitespace-pre-wrap text-slate-800">{texto}</p> : <Vazio />
    }
  }
}

/// Quem corrige, o quê e até quando, na resposta "NC". Confere a própria resposta:
/// no aparelho, a tratativa digitada fica guardada mesmo depois de a pessoa trocar
/// NC por C.
function Tratativa({ resposta }: { resposta: Resposta }) {
  const nc = resposta.texto === NAO_CONFORME || (resposta.opcoes ?? []).includes(NAO_CONFORME)
  if (!nc || (!resposta.ncResponsavel && !resposta.ncAcao && !resposta.ncPrazo)) return null
  return (
    <div className="mt-2 rounded border border-alta/30 bg-alta/5 p-2 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wide text-alta">Não conformidade</p>
      <dl className="mt-1 space-y-1">
      <div>
        <dt className="text-xs text-slate-500">Responsável</dt>
        <dd className="text-slate-800">
          {resposta.ncResponsavel || '—'}
          {resposta.ncResponsavel && !resposta.ncResponsavelId && (
            <span className="ml-1 text-xs text-slate-500">(digitado, não está na lista)</span>
          )}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-slate-500">Ação a ser tomada</dt>
        <dd className="whitespace-pre-wrap text-slate-800">{resposta.ncAcao || '—'}</dd>
      </div>
      <div>
        <dt className="text-xs text-slate-500">Prazo</dt>
        <dd className="text-slate-800">
          {resposta.ncPrazo ? new Date(`${resposta.ncPrazo}T00:00:00`).toLocaleDateString('pt-BR') : '—'}
        </dd>
      </div>
      </dl>
    </div>
  )
}

function ImagemDaResposta({ resposta }: { resposta: Resposta }) {
  const { estado, url } = useArquivo(resposta.arquivoId)
  if (estado === 'carregando') return <div className="mt-1 h-24 w-40 animate-pulse rounded bg-slate-100" />
  if (estado === 'ausente') return <p className="text-slate-400">Imagem indisponível sem conexão</p>
  return (
    <img
      src={url}
      alt={rotuloTipoCampo[resposta.campoTipo]}
      loading="lazy"
      className="mt-1 max-h-48 rounded border border-slate-200 object-contain"
    />
  )
}

function Vazio() {
  return <p className="text-slate-400">—</p>
}
